import { cn } from '@/client/lib/utils';
import type { LucideIcon } from 'lucide-react';

type Tone = 'default' | 'primary' | 'good' | 'warn' | 'bad';

const TONES: Record<Tone, string> = {
  default: 'bg-muted text-muted-foreground',
  primary: 'bg-primary/10 text-primary',
  good: 'bg-good-bg text-good',
  warn: 'bg-warn-bg text-warn',
  bad: 'bg-bad-bg text-bad',
};

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  icon?: LucideIcon;
  tone?: Tone;
  className?: string;
  onClick?: () => void;
  active?: boolean;
}

/** Compact KPI tile used across list and detail pages. */
export function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = 'default',
  className,
  onClick,
  active,
}: StatCardProps) {
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'group relative flex flex-col gap-3 overflow-hidden rounded-xl border border-border/70 bg-card p-4 text-left shadow-soft transition-all duration-200',
        onClick && 'hover:-translate-y-px hover:border-primary/30 hover:shadow-elevated',
        active && 'border-primary/40 ring-[3px] ring-primary/10',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-[12.5px] font-medium text-muted-foreground">{label}</p>
        {Icon && (
          <span className={cn('flex h-7 w-7 items-center justify-center rounded-lg', TONES[tone])}>
            <Icon className="h-3.5 w-3.5" />
          </span>
        )}
      </div>
      <div>
        <p className="truncate text-[22px] font-semibold leading-none tracking-[-0.02em] tabular-nums sm:text-[26px]">
          {value}
        </p>
        {hint && <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
    </Comp>
  );
}
